import { useMedia } from "shared/providers";
import {
  Button,
  ButtonSize,
  IconButton,
  IconButtonSize,
  Show
} from "shared/components";
import {
  ArrowNarrowLeft,
  ArrowNarrowRight,
  ChevronLeft,
  ChevronLeftDouble,
  ChevronRight,
  ChevronRightDouble
} from "untitledui-js-base";
import { cx } from "utils/classnames";

const maxPagesDesktop = 6;
const maxPagesMobile = 3;
const maxPagesAheadDesktop = 3;
const maxPagesAheadMobile = 1;

type PageItem = { type: "page"; page: number } | { type: "gap"; key: string };

// The first and last pages, the current one and its neighbours, with "…" for
// each run left out. A run of a single page shows that page instead, since a
// gap would take as much room.
const getGapPageItems = (total: number, current: number): PageItem[] => {
  const shown = Array.from({ length: total }, (_, idx) => idx + 1).filter(
    (page) => page === 1 || page === total || Math.abs(page - current) <= 1
  );

  return shown.flatMap((page, idx): PageItem[] => {
    const previous = shown[idx - 1];
    if (previous === undefined || page - previous === 1)
      return [{ type: "page", page }];
    if (page - previous === 2)
      return [
        { type: "page", page: page - 1 },
        { type: "page", page }
      ];
    return [
      { type: "gap", key: `gap-${previous}` },
      { type: "page", page }
    ];
  });
};

export const Pagination = ({
  total,
  current,
  className,
  onChange,
  disabled,
  showEdgeButtons = true,
  pageNumbers = "window"
}: {
  total: number;
  current: number;
  className?: string;
  onChange: (newPage: number) => void;
  disabled?: boolean;
  showEdgeButtons?: boolean;
  // "window": a sliding run of up to six pages. "gaps": the first and last
  // pages and the current one's neighbours, with "…" in between.
  pageNumbers?: "window" | "gaps";
}) => {
  const { isMobile } = useMedia();
  if (!total || total === 1) return null;

  const canGoNext = current < total;
  const canGoBack = current > 1;
  const canGoFirst = current > 1;
  const canGoLast = current < total;

  return (
    <div className={cx("flex w-full items-center gap-2", className)}>
      <Show if={showEdgeButtons}>
        <IconButton
          size={IconButtonSize.SM}
          onClick={() => {
            if (canGoFirst) onChange(1);
          }}
          disabled={!canGoFirst || disabled}
          Icon={ChevronLeftDouble}
          className="text-xs shrink-0"
        />
      </Show>
      <Button
        size={ButtonSize.SM}
        onClick={() => {
          if (canGoBack) onChange(current - 1);
        }}
        disabled={!canGoBack || disabled}
        Icon={ArrowNarrowLeft}
        className="text-xs shrink-0 md:flex hidden"
      >
        Prev
      </Button>

      <IconButton
        size={IconButtonSize.SM}
        onClick={() => {
          if (canGoBack) onChange(current - 1);
        }}
        disabled={!canGoBack || disabled}
        Icon={ChevronLeft}
        className="text-xs shrink-0 block md:hidden"
      />

      {pageNumbers === "gaps" ? (
        <div className="flex items-center gap-2">
          {getGapPageItems(total, current).map((item) =>
            item.type === "gap" ? (
              <span
                key={item.key}
                aria-hidden="true"
                className="px-1 text-xs font-medium text-content-400"
              >
                …
              </span>
            ) : (
              <Button
                key={item.page}
                size={ButtonSize.SM}
                disabled={disabled}
                aria-current={current === item.page ? "page" : undefined}
                onClick={() => onChange(item.page)}
                className={cx(
                  "max-w-[40px] text-xs",
                  current === item.page ? "border-2 border-primary-500" : ""
                )}
              >
                {item.page}
              </Button>
            )
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="flex gap-2">
            {Array(total)
              .fill("")
              .map((_: undefined, idx) => {
                // show only 6 pages at a time
                // with current page in the middle
                const numberOfPagesToShow = isMobile
                  ? maxPagesMobile
                  : maxPagesDesktop;

                const maxPagesAhead = isMobile
                  ? maxPagesAheadMobile
                  : maxPagesAheadDesktop;

                if (total > numberOfPagesToShow)
                  if (current < 4) {
                    if (idx > numberOfPagesToShow - 1) return null;
                  } else if (current > total - maxPagesAhead) {
                    if (idx < total - numberOfPagesToShow) return null;
                  } else if (
                    idx < current - maxPagesAhead ||
                    idx > current + maxPagesAhead
                  )
                    return null;

                return (
                  <Button
                    key={String(idx) + "-" + String(total)}
                    size={ButtonSize.SM}
                    disabled={disabled}
                    onClick={() => {
                      onChange(idx + 1);
                    }}
                    className={cx(
                      "max-w-[40px] text-xs",
                      current === idx + 1 ? "border-2 border-primary-500" : ""
                    )}
                  >
                    {idx + 1}
                  </Button>
                );
              })}
          </div>
        </div>
      )}

      <Button
        size={ButtonSize.SM}
        onClick={() => {
          if (canGoNext) onChange(current + 1);
        }}
        disabled={!canGoNext || disabled}
        Icon={ArrowNarrowRight}
        iconPosition="right"
        className="text-xs shrink-0 hidden md:flex"
      >
        Next
      </Button>

      <IconButton
        size={IconButtonSize.SM}
        onClick={() => {
          if (canGoNext) onChange(current + 1);
        }}
        disabled={!canGoLast || disabled}
        Icon={ChevronRight}
        className="text-xs shrink-0 block md:hidden"
      />

      <Show if={showEdgeButtons}>
        <IconButton
          size={IconButtonSize.SM}
          onClick={() => {
            if (canGoLast) onChange(total);
          }}
          disabled={!canGoLast || disabled}
          Icon={ChevronRightDouble}
          className="text-xs shrink-0"
        />
      </Show>
    </div>
  );
};
